import React, { useEffect, useState } from 'react';
import './Academy.css';
import CustomHeader from '../components/layout/CustomHeader';
import CustomFooter from '../components/layout/CustomFooter';

const Academy = () => {
  useEffect(() => {
    // Add scroll animations
    const animateOnScroll = () => {
      const cards = document.querySelectorAll('.course-card');
      const triggerHeight = window.innerHeight * 0.8;

      cards.forEach(card => {
        const cardTop = card.getBoundingClientRect().top;
        if (cardTop < triggerHeight) {
          card.style.opacity = '1';
          card.style.transform = 'translateY(0)';
        }
      });
    };

    // Add parallax effect to floating shapes
    const handleScroll = () => {
      const scrolled = window.pageYOffset;
      const shapes = document.querySelectorAll('.shape');
      
      shapes.forEach((shape, index) => {
        const speed = (index + 1) * 0.5;
        shape.style.transform = `translateY(${scrolled * speed}px)`;
      });
    };

    // Add hover effects for cards
    const cards = document.querySelectorAll('.course-card');
    cards.forEach(card => {
      card.addEventListener('mouseenter', () => {
        card.style.background = 'rgba(255, 255, 255, 1)';
      });
      
      card.addEventListener('mouseleave', () => {
        card.style.background = 'rgba(255, 255, 255, 0.95)';
      });
    });

    // Add fade-in animation for new sections
    const handleScrollAnimations = () => {
      const elements = document.querySelectorAll('.fade-in');
      elements.forEach(el => {
        const rect = el.getBoundingClientRect();
        const isVisible = (rect.top <= window.innerHeight * 0.8);
        
        if (isVisible) {
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
        }
      });
    };

    // Initialize animations for new sections
    const elements = document.querySelectorAll('.fade-in');
    elements.forEach(el => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(20px)';
      el.style.transition = 'all 0.8s ease';
    });

    window.addEventListener('scroll', animateOnScroll);
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('scroll', handleScrollAnimations);
    window.addEventListener('load', animateOnScroll);
    handleScrollAnimations();

    return () => {
      window.removeEventListener('scroll', animateOnScroll);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', handleScrollAnimations);
    };
  }, []);

  const [regStatus, setRegStatus] = useState({ success: null, message: '' });

  // Interactive functions
  const showInquiry = (course) => {
    alert(`Thank you for your interest in our ${course} course! Our team will contact you shortly to discuss enrollment details and answer any questions you may have.`);
  };

  const showFeatures = (course) => {
    const features = {
      'Web Development': 'Our Web Development course includes hands-on projects, live coding sessions, and industry-standard tools and technologies. You\'ll build real-world applications and receive personalized feedback from expert instructors.',
      'Data Science': 'The Data Science program covers everything from basic statistics to advanced machine learning. You\'ll work with real datasets and learn to build predictive models using Python and popular data science libraries.',
      'Mobile App Development': 'Learn to build cross-platform mobile applications using React Native and native development tools. The course includes app design, development, testing, and deployment to app stores.'
    };
    alert(`${course} Course Details:\n\n${features[course]}`);
  };

  const showContactForm = () => {
    alert('Contact Form will open here. You can integrate this with your preferred contact form solution or CRM system.');
  };

  const handleRegistration = async (event) => {
    event.preventDefault();
    setRegStatus({ success: null, message: '' });
    const formData = new FormData(event.target);
    const data = Object.fromEntries(formData.entries());
    const payload = {
      name: data.fullName,
      email: data.email,
      phone: (data.countryCode || '') + (data.phone || ''),
      subject: `${data.category || ''} - ${data.course || ''}`,
      message: data.message || ''
    };
    try {
      const res = await fetch('/api/v1/forms/academy-register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await res.json();
      if (result.success) {
        setRegStatus({ success: true, message: result.message || 'Registration submitted successfully!' });
        event.target.reset();
      } else {
        setRegStatus({ success: false, message: result.message || 'Failed to submit registration. Please try again later.' });
      }
    } catch (err) {
      setRegStatus({ success: false, message: 'Failed to submit registration. Please try again later.' });
    }
  };

  return (
    <>
      <CustomHeader activePage="academy" />

      <div className="floating-shapes">
        <div className="shape"></div>
        <div className="shape"></div>
        <div className="shape"></div>
      </div>

      <div className="header">
        <div className="container">
          <h1>IntelUniVen Academy</h1>
          <p>Empowering the next generation of tech leaders through comprehensive training programs and hands-on learning experiences.</p>
        </div>
      </div>

      <div className="container">
        <div className="courses-grid">
          {/* Data Science Course */}
          <div className="course-card">
            <a href="/products#school-management" className="course-icon-link">
              <div className="course-icon">📊</div>
            </a>
            <h2 className="course-title">Data Science</h2>
            <p className="course-description">
              Dive into the world of data science and learn to extract valuable insights from complex datasets.
            </p>
            <ul className="features-list">
              <li>Python programming for data analysis</li>
              <li>Machine learning algorithms</li>
              <li>Data visualization techniques</li>
              <li>Statistical analysis methods</li>
              <li>Real-world project experience</li>
              <li>Career guidance and placement support</li>
            </ul>
            <div className="cta-buttons">
              <a href="#" className="btn btn-primary" onClick={() => showInquiry('Data Science')}>Enroll Now</a>
              <a href="#" className="btn btn-secondary" onClick={() => showFeatures('Data Science')}>Course Details</a>
            </div>
          </div>

          {/* Web Development Course */}
          <div className="course-card">
            <a href="/products#lms" className="course-icon-link">
              <div className="course-icon">💻</div>
            </a>
            <h2 className="course-title">Web Development</h2>
            <p className="course-description">
              Master modern web development with our comprehensive course covering front-end and back-end technologies.
            </p>
            <ul className="features-list">
              <li>HTML5, CSS3, and JavaScript fundamentals</li>
              <li>React.js and Node.js development</li>
              <li>Database design and management</li>
              <li>API development and integration</li>
              <li>Project-based learning approach</li>
              <li>Industry expert mentorship</li>
            </ul>
            <div className="cta-buttons">
              <a href="#" className="btn btn-primary" onClick={() => showInquiry('Web Development')}>Enroll Now</a>
              <a href="#" className="btn btn-secondary" onClick={() => showFeatures('Web Development')}>Course Details</a>
            </div>
          </div>

          {/* Mobile App Development */}
          <div className="course-card">
            <a href="/products#vehicle-servicing" className="course-icon-link">
              <div className="course-icon">📱</div>
            </a>
            <h2 className="course-title">Mobile App Development</h2>
            <p className="course-description">
              Learn to create powerful mobile applications for iOS and Android platforms using modern development tools.
            </p>
            <ul className="features-list">
              <li>React Native development</li>
              <li>Native iOS and Android development</li>
              <li>UI/UX design principles</li>
              <li>App store deployment</li>
              <li>Performance optimization</li>
              <li>Monetization strategies</li>
            </ul>
            <div className="cta-buttons">
              <a href="#" className="btn btn-primary" onClick={() => showInquiry('Mobile App Development')}>Enroll Now</a>
              <a href="#" className="btn btn-secondary" onClick={() => showFeatures('Mobile App Development')}>Course Details</a>
            </div>
          </div>
        </div>
      </div>

      <div className="upcoming-section fade-in">
        <div className="section-title">
          <h2>Upcoming Batches</h2>
          <p>Join our next training sessions and advance your career</p>
        </div>
        
        <div className="batch-grid">
          <div className="batch-card">
            <div className="batch-date">Starting June 15, 2025</div>
            <div className="batch-title">Java Full Stack Development</div>
            <div className="batch-details">Duration: 12 weeks | Mode: Hybrid | Seats: 25</div>
          </div>
          
          <div className="batch-card">
            <div className="batch-date">Starting June 20, 2025</div>
            <div className="batch-title">Python for Data Science</div>
            <div className="batch-details">Duration: 10 weeks | Mode: Online | Seats: 30</div>
          </div>
          
          <div className="batch-card">
            <div className="batch-date">Starting June 25, 2025</div>
            <div className="batch-title">Advanced Communication Skills</div>
            <div className="batch-details">Duration: 6 weeks | Mode: In-person | Seats: 20</div>
          </div>
          
          <div className="batch-card">
            <div className="batch-date">Starting July 1, 2025</div>
            <div className="batch-title">SAP FICO Certification</div>
            <div className="batch-details">Duration: 8 weeks | Mode: Hybrid | Seats: 15</div>
          </div>
          
          <div className="batch-card">
            <div className="batch-date">Starting July 5, 2025</div>
            <div className="batch-title">Interview Mastery Workshop</div>
            <div className="batch-details">Duration: 4 weeks | Mode: Online | Seats: 40</div>
          </div>
          
          <div className="batch-card">
            <div className="batch-date">Starting July 10, 2025</div>
            <div className="batch-title">Business English Excellence</div>
            <div className="batch-details">Duration: 8 weeks | Mode: In-person | Seats: 25</div>
          </div>
        </div>
      </div>

      <div className="registration-section fade-in">
        <h2>Register for Training</h2>
        <p>Take the first step towards your professional growth</p>
        
        <form className="registration-form" onSubmit={handleRegistration}>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="fullName">Full Name</label>
              <input type="text" id="fullName" name="fullName" required />
            </div>
            
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input type="email" id="email" name="email" required />
            </div>
            
            <div className="form-group">
              <label htmlFor="phone">Phone Number</label>
              <div className="phone-input-group">
                <select id="countryCode" name="countryCode" required>
                  <option value="+91">+91 (IN)</option>
                  <option value="+1">+1 (US)</option>
                  <option value="+44">+44 (UK)</option>
                  <option value="+61">+61 (AU)</option>
                  <option value="+86">+86 (CN)</option>
                </select>
                <input type="tel" id="phone" name="phone" placeholder="Enter phone" required />
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="experience">Work Experience</label>
              <select id="experience" name="experience" required>
                <option value="">Select Experience Level</option>
                <option value="fresher">Fresher (0-1 years)</option>
                <option value="junior">Junior (1-3 years)</option>
                <option value="mid">Mid-level (3-7 years)</option>
                <option value="senior">Senior (7+ years)</option>
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="category">Training Category</label>
              <select id="category" name="category" required>
                <option value="">Select Category</option>
                <option value="technology">Technology</option>
                <option value="soft-skills">Soft Skills</option>
                <option value="nlp">NLP & Communication</option>
                <option value="english">English Proficiency</option>
                <option value="career">Career Development</option>
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="course">Preferred Course</label>
              <select id="course" name="course" required>
                <option value="">Select Course</option>
                <option value="java">Java Full Stack</option>
                <option value="python">Python Development</option>
                <option value="sap">SAP Training</option>
                <option value="communication">Communication Skills</option>
                <option value="interview">Interview Skills</option>
              </select>
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="message">Additional Requirements/Message</label>
            <textarea id="message" name="message" placeholder="Tell us about your specific learning goals or any questions you have..."></textarea>
          </div>
          
          <button type="submit" className="submit-btn">Register Now</button>
          {regStatus.message && (
            <div style={{ marginTop: '1rem', color: regStatus.success ? 'green' : 'red', fontWeight: 500 }}>
              {regStatus.message}
            </div>
          )}
        </form>
      </div>

      <div className="contact-section">
        <div className="container">
          <h2>Ready to Start Your Learning Journey?</h2>
          <p>Join our academy and transform your career with industry-leading training programs</p>
          <button className="contact-btn" onClick={showContactForm}>Get Started</button>
        </div>
      </div>

      <CustomFooter />
    </>
  );
};

export default Academy; 